import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProfileCard = ({ 
  profile, 
  onViewProfile, 
  onSendMessage, 
  onExpressInterest,
  userGender = 'male',
  hasActiveSubscription = false,
  isLiked = false
}) => {
  const [isInterested, setIsInterested] = useState(isLiked);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setIsInterested(isLiked);
  }, [isLiked]);

  const handleExpressInterest = () => {
    setIsInterested(!isInterested);
    onExpressInterest(profile?.id);
  };

  const handleSendMessage = () => {
    if (userGender === 'female' || hasActiveSubscription) {
      onSendMessage(profile?.id);
    } else {
      window.location.href = '/subscription-payment';
    }
  };

  const getBlurClass = () => {
    switch (profile?.privacyLevel) {
      case 'high':
        return 'blur-lg';
      case 'medium':
        return 'blur-md';
      case 'low':
        return 'blur-sm';
      default:
        return '';
    }
  };

  const getCompatibilityColor = () => {
    if (profile?.compatibility >= 80) return 'text-success';
    if (profile?.compatibility >= 60) return 'text-warning';
    return 'text-muted-foreground';
  };

  // Calculate trust score based on verification factors
  const calculateTrustScore = () => {
    let score = 50; // Base score
    if (profile?.isVerified) score += 20;
    if (profile?.idVerified) score += 15;
    if (profile?.photoVerified) score += 10;
    if (profile?.backgroundCheck) score += 5;
    return Math.min(score, 100);
  };

  const trustScore = calculateTrustScore();

  const getTrustScoreColor = () => {
    if (trustScore >= 85) return 'text-success';
    if (trustScore >= 70) return 'text-primary';
    if (trustScore >= 50) return 'text-warning';
    return 'text-muted-foreground';
  };

  const getTrustScoreBgColor = () => {
    if (trustScore >= 85) return 'bg-success/10';
    if (trustScore >= 70) return 'bg-primary/10';
    if (trustScore >= 50) return 'bg-warning/10';
    return 'bg-muted';
  };

  const hasPhoto = profile?.image && profile?.image?.trim() !== '';
  const showPlaceholder = !hasPhoto || !imageLoaded;

  return (
    <div className="bg-card rounded-xl shadow-warm hover:shadow-warm-md transition-smooth overflow-hidden group">
      <div className="relative aspect-[3/4] overflow-hidden">
        {/* Non-white placeholder for users without a profile picture */}
        {showPlaceholder ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-200 via-slate-100 to-primary/30 dark:from-slate-700 dark:via-slate-600 dark:to-primary/40">
            <div className="w-20 h-20 rounded-full bg-slate-300/80 dark:bg-slate-500/80 flex items-center justify-center mb-2">
              <Icon name="User" size={40} className="text-slate-500 dark:text-slate-300" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 max-w-[80%] truncate px-2">
              {profile?.displayName || 'No photo'}
            </span>
          </div>
        ) : null}
        {hasPhoto && (
          <Image
            src={profile?.image}
            alt={profile?.imageAlt}
            className={`w-full h-full object-cover transition-smooth group-hover:scale-105 ${
              profile?.photoBlurred ? getBlurClass() : ''
            } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
          />
        )}
        {hasPhoto && !imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-200 to-primary/20">
            <Icon name="User" size={48} className="text-slate-500" />
          </div>
        )}

        {/* Enhanced Verification Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          {profile?.isVerified && (
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/90 backdrop-blur-sm rounded-full">
              <Icon name="BadgeCheck" size={14} color="#FFFFFF" />
              <span className="text-xs font-medium text-white">Verified</span>
            </div>
          )}
          {profile?.identityVerificationStatus === 'approved' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-600/90 backdrop-blur-sm rounded-full">
              <Icon name="CreditCard" size={14} color="#FFFFFF" />
              <span className="text-xs font-medium text-white">ID</span>
            </div>
          )}
          {profile?.photoVerificationStatus === 'approved' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-purple-600/90 backdrop-blur-sm rounded-full">
              <Icon name="Camera" size={14} color="#FFFFFF" />
              <span className="text-xs font-medium text-white">Photo</span>
            </div>
          )}
          {profile?.backgroundCheck && (
            <div className="flex items-center gap-1 px-2 py-1 bg-green-600/90 backdrop-blur-sm rounded-full">
              <Icon name="ShieldCheck" size={14} color="#FFFFFF" />
              <span className="text-xs font-medium text-white">Safe</span>
            </div>
          )}
        </div>

        {/* Dark overlay so name is always visible on any photo or placeholder */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 via-black/50 to-transparent p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg md:text-xl font-heading font-semibold text-white truncate drop-shadow-sm">
                  {profile?.displayName}
                </h3>
                {profile?.isOnline && (
                  <span className="w-2.5 h-2.5 bg-success rounded-full flex-shrink-0 shadow-sm border border-black/20" title="Online now" />
                )}
              </div>
              <div className="flex items-center gap-2 text-white/95 text-sm">
                <span>{profile?.age}</span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  <span className="truncate">{profile?.location}</span>
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 bg-card/90 backdrop-blur-sm rounded-lg ${getCompatibilityColor()}`}>
              <Icon name="Heart" size={14} />
              <span className="text-xs font-bold">{profile?.compatibility}%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {/* Trust Score Indicator */}
        <div className={`flex items-center justify-between p-2 rounded-lg ${getTrustScoreBgColor()}`}>
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={16} className={getTrustScoreColor()} />
            <span className="text-sm font-medium">Trust Score</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${trustScore >= 85 ? 'bg-success' : trustScore >= 70 ? 'bg-primary' : trustScore >= 50 ? 'bg-warning' : 'bg-muted-foreground'}`}
                style={{ width: `${trustScore}%` }}
              />
            </div>
            <span className={`text-sm font-bold ${getTrustScoreColor()}`}>
              {trustScore}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Users" size={14} />
          <span>{profile?.relationshipStatus}</span>
          {profile?.lastActive && (
            <>
              <span>•</span>
              <span>Active {profile?.lastActive}</span>
            </>
          )}
        </div>

        {profile?.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {profile?.bio}
          </p>
        )}

        {profile?.interests && profile?.interests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {profile?.interests?.slice(0, 3)?.map((interest, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted rounded-full text-xs font-medium"
              >
                {interest}
              </span>
            ))}
            {profile?.interests?.length > 3 && (
              <span className="px-2 py-1 bg-muted rounded-full text-xs font-medium">
                +{profile?.interests?.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant={isInterested ? 'default' : 'outline'}
            size="sm"
            iconName={isInterested ? 'HeartOff' : 'Heart'}
            iconPosition="left"
            onClick={handleExpressInterest}
            className="flex-1"
          >
            {isInterested ? 'Interested' : 'Like'}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleSendMessage}
            className="flex-1"
          >
            Message
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Eye"
            onClick={() => onViewProfile(profile?.id)}
          />
        </div>

        {userGender === 'male' && !hasActiveSubscription && (
          <div className="flex items-center gap-2 p-2 bg-warning/10 rounded-lg">
            <Icon name="Lock" size={14} color="var(--color-warning)" />
            <p className="text-xs text-warning">
              Subscribe to send unlimited messages
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileCard;